export interface ITask {
  id: number;
  category: string;
  type: string;
  params: JSON;
  task: number;
  hasSucceeded: boolean;
  succeededAt: Date;
  isCancelled: boolean;
  cancelledAt: Date;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
  accountId: number;
  userId: number;
  contactId: number;
  patientId: number;
}

export interface ISuspensionDates {
  startDate?: string;
  endDate?: string;
}

// Utilities

const safeParseParams = (params: string | object | null): any | null => {
  if (!params) return null;
  try {
    return typeof params === 'string' ? JSON.parse(params) : params;
  } catch {
    return null;
  }
};

// Get suspension dates from task objects
export const getSuspensionDates = (tasks: ITask[]): ISuspensionDates | null => {
  let startDate: string | undefined;
  let endDate: string | undefined;
  if (tasks && tasks.length > 0) {
    tasks.forEach((task) => {
      if (!task) return;
      if (task.params) {
        const params = safeParseParams(task.params);
        if (!params) return;
        if (params.startDate) {
          startDate = params.startDate;
        }
        if (params.endDate) {
          endDate = params.endDate;
        }
      }
    });
    return { startDate, endDate };
  } else {
    return null;
  }
};

/**
 * Extract the list of app keys that are currently paused
 * from active suspension tasks.
 */
export const getPausedApps = (tasks: ITask[]): string[] => {
  const pausedApps: string[] = [];
  if (!tasks || tasks.length === 0) return pausedApps;
  tasks.forEach((task) => {
    if (!task) return;
    if (task.params) {
      const params = safeParseParams(task.params);
      if (!params) return;
      if (params.app && !pausedApps.includes(params.app)) {
        pausedApps.push(params.app);
      }
    }
  });
  return pausedApps;
};

/**
 * Check if the suspension is a full-patient pause (no app-specific tasks)
 * or program-specific.
 */
export const isFullPause = (tasks: ITask[]): boolean => {
  if (!tasks || tasks.length === 0) return true;
  for (const task of tasks) {
    if (!task) continue;
    if (task.params) {
      const params = safeParseParams(task.params);
      if (!params) continue;
      if (!params.app) {
        return true; // At least one task has no app = full pause
      }
    }
  }
  return false;
};

/**
 * Get suspension dates for a specific app from task objects.
 */
export const getSuspensionDatesForApp = (
  tasks: ITask[],
  app: string
): ISuspensionDates | null => {
  let startDate: string | undefined;
  let endDate: string | undefined;
  if (tasks && tasks.length > 0) {
    tasks.forEach((task) => {
      if (!task) return;
      if (task.params) {
        const params = safeParseParams(task.params);
        if (!params) return;
        if (params.app !== app) return;
        if (params.startDate) startDate = params.startDate;
        if (params.endDate) endDate = params.endDate;
      }
    });
    if (startDate || endDate) return { startDate, endDate };
  }
  return null;
};
